(ns practitest.frontend.components2.dashboard.events
  (:require [practitest.frontend.components2.dashboard.helpers :as helpers]
            [practitest.frontend.components2.dashboard.schema :as sc]
            [practitest.frontend.common.re-frame :as rf-common]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.events :as events]
            [practitest.frontend.components2.dashboard.chart :as-alias chart]
            [practitest.frontend.components2.dashboard.events.bar :as-alias bar]
            [practitest.frontend.components2.dashboard.helpers :as h]
            [re-frame.core :as rf]))

(defmulti transform
  (fn [evt _data] evt))

(defn transform-common
  [evt {:as   item
        :keys [id title data entity-name-plural item-url x-values-map y-values-map status-colors warning]

        {:keys [colors
                cell-width
                cell-height
                legend-location
                percentage-mode
                remove-empty-values]} :highcharts-options}]
  (let [item' (cond-> {:chart/id                  id
                       :chart/title               title
                       :chart/sub-title           (helpers/filters-title item)
                       :chart/item-route          (helpers/parse-url item-url)
                       :chart/entity-name         entity-name-plural
                       :chart/warning             warning
                       :chart/x-values-map        x-values-map
                       :chart/y-values-map        y-values-map
                       :chart/user-colors         (helpers/parse-colors colors)
                       :chart/status-colors       (into {} status-colors)
                       :chart/percentage-mode     (helpers/str->bool percentage-mode)
                       :chart/cell-width          cell-width
                       :chart/cell-height         cell-height
                       :chart/legend-location     legend-location
                       :chart/remove-empty-values (helpers/str->bool remove-empty-values)}
                (some? data) (assoc :chart/data data))]
    (merge item' (transform evt item'))))

(def load-chart
  {:merge-by   :chart/id
   :transform  transform-common
   :db-path    [:dashboard-items]
   :url-based? true})

(def load-pie
  {:evt    ::load-pie-data
   :schema sc/PieChart})

(def load-bar
  {:evt    ::load-bar-data
   :schema sc/BarChart})

(def load-planned-vs-executed
  {:evt    ::load-planned-vs-executed-data
   :schema sc/LineChart})

(def load-stacked-bar
  {:evt    ::load-stacked-bar-data
   :schema sc/StackedBarChart})

(def load-age
  {:evt    ::load-age-data
   :schema sc/AgeChart})

(def load-gauge-chart
  {:evt    ::load-gauge-chart-data
   :schema sc/GaugeChart})

(def load-stacked-progress-bar
  {:evt    ::load-stacked-progress-bar-data
   :schema sc/StackedProgressBarChart})

(def load-table-chart
  {:evt    ::load-table-chart-data
   :schema sc/TableChart})

(def load-status-changes-data
  {:evt    ::load-status-changes-data
   :schema sc/LineChart})

;; Dashboard item type (as sent by the backend) -> chart load event
(def item-type->load-event
  {"pie"                 ::load-pie-data
   "bar"                 ::load-bar-data
   "progress"            ::load-status-changes-data
   "execution_stats"     ::load-stacked-progress-bar-data
   "stacked_bar"         ::load-stacked-bar-data
   "defect_age"          ::load-age-data
   "planned_vs_executed" ::load-planned-vs-executed-data
   "gauge"               ::load-gauge-chart-data
   "traceability"        ::load-table-chart-data
   "latest"              ::load-table-chart-data
   "distribution"        ::load-table-chart-data
   "step_status_pie"     ::load-pie-data})

(defn loaded-event
  "Success event registered by reg-load-data-events for `load-evt`, so callers that fetch
   the data themselves (e.g. to track per-item errors) can hand the response to the chart."
  [load-evt]
  (keyword (namespace load-evt) (str (name load-evt) "-successed")))

(doseq [evt [load-pie
             load-bar
             load-planned-vs-executed
             load-stacked-bar
             load-age
             load-gauge-chart
             load-stacked-progress-bar
             load-table-chart
             load-status-changes-data]]
  (rf-common/reg-load-data-events (merge load-chart evt)))

(rf/reg-event-db
 ::toggle-legend-item
 (fn [db [_ id item]]
   (update-in db [:dashboard-items id :chart/toggle-legend item] (fnil not true))))

(rf/reg-event-fx
 ::navigate
 (fn [_ [_ {:chart/keys [item-route]} {:keys [url-params]}]]
   (when-let [url (h/url-for item-route url-params)]
     {::events/navigate url})))

(defn- series-chart-transform [chart-type {:chart/keys [data]}]
  {:chart/type          chart-type
   :chart/legend-labels (mapv :name (:series data))})

(defmethod transform ::load-pie-data [_ {:chart/keys [data]}]
  {:chart/type          ::chart/pie
   :chart/legend-labels (mapv :category data)})

(defmethod transform ::load-planned-vs-executed-data [_ item]
  (assoc (series-chart-transform ::chart/line item)
         :chart/caption (get-translation "dashboards.planned-executed.chart.comment"
                                          "Planned lines shows only instances with Planned Executed Date")))

(defmethod transform ::load-bar-data [_ item]
  (series-chart-transform ::chart/bar item))

(defmethod transform ::load-stacked-bar-data [_ item]
  (series-chart-transform ::chart/stacked-bar item))

(defmethod transform ::load-age-data [_ item]
  (series-chart-transform ::chart/age item))

(defmethod transform ::load-gauge-chart-data [_ _]
  {:chart/type ::chart/gauge})

(defmethod transform ::load-stacked-progress-bar-data [_ item]
  (series-chart-transform ::chart/stacked-progress-bar item))

(defmethod transform ::load-table-chart-data [_ _]
  {:chart/type ::chart/table})

(defmethod transform ::load-status-changes-data [_ item]
  (series-chart-transform ::chart/line item))